/* Losses and expenditures (FR-ANAL-09), reduced from the live event stream.
 *
 * Command PE ships this as a file EXPORT. A live view is the more useful shape: a
 * commander needs attrition and magazine state mid-exercise, not after it.
 *
 * ── No fixture behind it ────────────────────────────────────────────────────
 *
 * The panel used to seed itself from `data/attrition.ts` — four scripted kills that looked exactly
 * like a real casualty list. That file is gone. When the Gateway is unreachable this panel shows
 * nothing and says why, because the honest answer to "how many have we lost" is never a number
 * left over from a mockup.
 *
 * ── Two numbers that are deliberately not what they look like ──────────────
 *
 * Pk is shown as hits/fired and labelled "observed", not "probability". With single-digit shot
 * counts it is an observation, and calling it a probability invites reading a trend that is not
 * there.
 *
 * Rounds remaining is ABSENT, not zero, when no living platform still carries the weapon. An
 * empty magazine and a magazine at the bottom of the Baltic are different facts with different
 * remedies, and rendering the second as "0 left" reads as a resupply problem.
 */
import { useEffect, useState } from 'react'
import { Crosshair, Skull, X } from 'lucide-react'
import { useDeck } from '../state/store'
import {
  EMPTY_ATTRITION,
  causeLabel,
  fetchAttrition,
  lossClock,
  type Attrition,
} from '../lib/liveAttrition'
import { thousands } from '../lib/format'

type Tab = 'losses' | 'expenditures'

export function LossesPanel() {
  const open = useDeck((s) => s.lossesOpen)
  const toggle = useDeck((s) => s.toggleLosses)
  const epochIso = useDeck((s) => s.epochIso)
  const me = useDeck((s) => s.me)
  const [tab, setTab] = useState<Tab>('losses')
  const [data, setData] = useState<Attrition>(EMPTY_ATTRITION)
  const [error, setError] = useState('')

  useEffect(() => {
    // Polled while open, not carried on the snapshot. Attrition changes on the order of once a
    // minute; the snapshot goes out five times a second, and growing every frame of the hot path
    // for a panel almost nobody has open is the wrong trade.
    if (!open) return
    let cancelled = false
    const load = async () => {
      try {
        const next = await fetchAttrition()
        if (!cancelled) {
          setData(next)
          setError('')
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e))
      }
    }
    void load()
    const t = window.setInterval(load, 2000)
    return () => {
      cancelled = true
      window.clearInterval(t)
    }
  }, [open])

  if (!open) return null

  // Whose losses are "ours". An umpire sees both sides and has no side of its own, so it reads the
  // table from BLUE's point of view — an exchange ratio has to be stated from somewhere, and one
  // labelled by side name cannot be misread.
  const ownSide = me?.side || 'blue'
  const { losses, expenditures } = data
  const ownLosses = losses.filter((l) => l.side === ownSide)
  const otherLosses = losses.filter((l) => l.side !== ownSide)
  const totalFired = expenditures.reduce((a, e) => a + e.fired, 0)
  const totalHits = expenditures.reduce((a, e) => a + e.hits, 0)

  return (
    <div
      className="panel absolute left-3 top-3 z-20 flex w-[26rem] flex-col overflow-hidden"
      style={{ maxHeight: 'calc(100% - 24px)' }}
    >
      <div
        className="flex shrink-0 items-center justify-between px-3 py-2"
        style={{ borderBottom: '1px solid var(--color-line-2)' }}
      >
        <span className="title">Losses &amp; expenditures</span>
        <button
          className="ctl"
          style={{ width: 20, height: 20, padding: 0 }}
          onClick={toggle}
          aria-label="Close losses and expenditures panel"
        >
          <X size={12} />
        </button>
      </div>

      {/* Headline exchange ratio */}
      <div className="flex shrink-0" style={{ borderBottom: '1px solid var(--color-line-2)' }}>
        <div className="flex-1 px-3 py-2">
          <div className="label" style={{ fontSize: 9, color: 'var(--color-friend)' }}>
            {ownSide.toUpperCase()} losses
          </div>
          <div className="num" style={{ fontSize: 20, color: 'var(--color-text-1)' }}>
            {ownLosses.length}
          </div>
        </div>
        <div className="flex-1 px-3 py-2" style={{ borderLeft: '1px solid var(--color-line-1)' }}>
          <div className="label" style={{ fontSize: 9, color: 'var(--color-hostile)' }}>
            Opposing losses
          </div>
          <div className="num" style={{ fontSize: 20, color: 'var(--color-text-1)' }}>
            {otherLosses.length}
          </div>
        </div>
        <div className="flex-1 px-3 py-2" style={{ borderLeft: '1px solid var(--color-line-1)' }}>
          <div className="label" style={{ fontSize: 9 }}>
            Exchange
          </div>
          <div className="num" style={{ fontSize: 20, color: 'var(--color-text-1)' }}>
            {ownLosses.length === 0 ? '—' : (otherLosses.length / ownLosses.length).toFixed(1)}
            <span className="unit" style={{ fontSize: 10 }}>:1</span>
          </div>
        </div>
      </div>

      <div className="flex shrink-0" style={{ borderBottom: '1px solid var(--color-line-2)' }} role="tablist">
        {(['losses', 'expenditures'] as Tab[]).map((t) => {
          const active = tab === t
          return (
            <button
              key={t}
              role="tab"
              aria-selected={active}
              onClick={() => setTab(t)}
              className="flex flex-1 items-center justify-center gap-1.5 py-1.5 transition-colors"
              style={{
                fontSize: 11,
                fontWeight: active ? 500 : 400,
                color: active ? 'var(--color-text-1)' : 'var(--color-text-3)',
                background: active ? 'var(--color-accent-dim)' : undefined,
                borderBottom: `2px solid ${active ? 'var(--color-accent)' : 'transparent'}`,
              }}
            >
              {t === 'losses' ? <Skull size={11} aria-hidden /> : <Crosshair size={11} aria-hidden />}
              {t === 'losses' ? `Losses (${losses.length})` : `Expenditures (${thousands(totalFired)})`}
            </button>
          )
        })}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {tab === 'losses' && losses.length === 0 && (
          <p className="px-3 py-3" style={{ fontSize: 10.5, color: 'var(--color-text-3)' }}>
            {error ? 'Attrition unavailable — the Gateway did not answer.' : 'No losses yet.'}
          </p>
        )}

        {tab === 'losses' && losses.length > 0 && (
          <table className="w-full" style={{ borderCollapse: 'collapse' }}>
            <caption className="sr-only">Platform losses by side, with time and cause</caption>
            <thead>
              <tr>
                {['Time', 'Platform', 'Cause'].map((h) => (
                  <th
                    key={h}
                    scope="col"
                    className="label px-3 py-1 text-left"
                    style={{ fontSize: 8.5, borderBottom: '1px solid var(--color-line-2)' }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {losses.map((l) => (
                <tr key={l.id} style={{ borderBottom: '1px solid var(--color-line-1)' }}>
                  <td className="num px-3 py-1 align-top" style={{ fontSize: 10, color: 'var(--color-text-3)', whiteSpace: 'nowrap' }}>
                    {lossClock(epochIso, l.simTimeNs)}
                  </td>
                  <td className="px-3 py-1 align-top">
                    <span
                      className="num block"
                      style={{
                        fontSize: 11,
                        color: l.side === ownSide ? 'var(--color-friend)' : 'var(--color-hostile)',
                      }}
                    >
                      {l.designation}
                    </span>
                    <span className="block" style={{ fontSize: 9.5, color: 'var(--color-text-3)' }}>
                      {l.className}
                    </span>
                  </td>
                  <td className="px-3 py-1 align-top" style={{ fontSize: 10, color: 'var(--color-text-2)' }}>
                    {causeLabel(l)}
                    {l.killedBy && (
                      <span className="num block" style={{ fontSize: 9.5, color: 'var(--color-text-3)' }}>
                        by {l.killedBy}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {tab === 'expenditures' && expenditures.length === 0 && (
          <p className="px-3 py-3" style={{ fontSize: 10.5, color: 'var(--color-text-3)' }}>
            {error
              ? 'Magazine state unavailable — the Gateway did not answer.'
              : 'No magazines reported.'}
          </p>
        )}

        {tab === 'expenditures' && expenditures.length > 0 && (
          <table className="w-full" style={{ borderCollapse: 'collapse' }}>
            <caption className="sr-only">Munitions fired, hits observed and rounds remaining</caption>
            <thead>
              <tr>
                {[
                  ['Weapon', 'left'],
                  ['Fired', 'right'],
                  ['Hits', 'right'],
                  ['Obs. Pk', 'right'],
                  ['Left', 'right'],
                ].map(([h, align]) => (
                  <th
                    key={h}
                    scope="col"
                    className="label px-2 py-1"
                    style={{
                      fontSize: 8.5,
                      textAlign: align as 'left' | 'right',
                      borderBottom: '1px solid var(--color-line-2)',
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {expenditures.map((e) => {
                const pk = e.fired > 0 ? e.hits / e.fired : null
                return (
                  <tr key={e.id} style={{ borderBottom: '1px solid var(--color-line-1)' }}>
                    <td className="px-2 py-1">
                      <span className="num block truncate" style={{ fontSize: 11 }}>
                        {e.weapon}
                      </span>
                      <span className="label block" style={{ fontSize: 8 }}>
                        {e.side.toUpperCase()} · {e.category}
                      </span>
                    </td>
                    <td className="num px-2 py-1 text-right" style={{ fontSize: 11 }}>
                      {thousands(e.fired)}
                    </td>
                    <td className="num px-2 py-1 text-right" style={{ fontSize: 11 }}>
                      {thousands(e.hits)}
                    </td>
                    <td
                      className="num px-2 py-1 text-right"
                      style={{ fontSize: 11, color: pk === null ? 'var(--color-text-3)' : 'var(--color-text-2)' }}
                      title={
                        e.fired < 10
                          ? `${e.hits} of ${e.fired} — too few shots to read as a rate`
                          : `${e.hits} of ${e.fired}`
                      }
                    >
                      {pk === null ? '—' : pk.toFixed(2)}
                    </td>
                    <td
                      className="num px-2 py-1 text-right"
                      style={{
                        fontSize: 11,
                        color:
                          e.remaining === undefined
                            ? 'var(--color-text-3)'
                            : e.remaining === 0
                              ? 'var(--color-crit)'
                              : e.remaining < 4
                                ? 'var(--color-warn)'
                                : 'var(--color-text-2)',
                      }}
                      title={
                        e.remaining === undefined
                          ? 'No surviving platform carries this weapon'
                          : undefined
                      }
                    >
                      {e.remaining === undefined ? '—' : thousands(e.remaining)}
                    </td>
                  </tr>
                )
              })}
            </tbody>
            <tfoot>
              <tr style={{ background: 'var(--color-surface-2)' }}>
                <td className="label px-2 py-1" style={{ fontSize: 9 }}>
                  Total
                </td>
                <td className="num px-2 py-1 text-right" style={{ fontSize: 11 }}>
                  {thousands(totalFired)}
                </td>
                <td className="num px-2 py-1 text-right" style={{ fontSize: 11 }}>
                  {thousands(totalHits)}
                </td>
                <td className="num px-2 py-1 text-right" style={{ fontSize: 11 }}>
                  {totalFired ? (totalHits / totalFired).toFixed(2) : '—'}
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
        )}
      </div>

      <p
        className="shrink-0 px-3 py-1.5"
        style={{ fontSize: 9.5, color: 'var(--color-text-3)', borderTop: '1px solid var(--color-line-2)' }}
      >
        {error
          ? `Live attrition unavailable (${error}). Nothing is shown rather than a stale figure.`
          : tab === 'expenditures'
            ? 'Observed Pk is hits ÷ fired, not a modelled probability. At these shot counts it is an observation, not a rate.'
            : 'Reduced from the event stream. A kill you claim is shown as the track you held, not the platform it was.'}
      </p>
    </div>
  )
}
